"use client";

import { getFirebaseAuth } from "@/lib/firebase/client";

/**
 * The last token getIdToken() resolved, so a caller that cannot await — an unload handler —
 * can still authenticate its request. See getCachedIdToken().
 */
let cachedIdToken: string | null = null;

/**
 * Current user's Firebase ID token, or null when signed out.
 * The SDK refreshes the token transparently when it is close to expiring.
 */
export async function getIdToken(): Promise<string | null> {
  if (typeof window === "undefined") return null;
  try {
    const user = getFirebaseAuth().currentUser;
    if (!user) {
      cachedIdToken = null;
      return null;
    }
    cachedIdToken = await user.getIdToken();
    return cachedIdToken;
  } catch (error) {
    console.error("Failed to get Firebase ID token:", error);
    return null;
  }
}

/**
 * The most recent ID token, synchronously.
 *
 * Only for code that runs while the page is going away. `pagehide` and a `visibilitychange` to
 * hidden are the last chance to send anything, and awaiting getIdToken() there loses the request:
 * the continuation after the await frequently never runs, so fetch() is never reached and
 * `keepalive` has nothing to keep alive. Everything else should await getIdToken(), which refreshes
 * an expiring token; this can hand back one that has just expired, and the route answers 401.
 */
export function getCachedIdToken(): string | null {
  return cachedIdToken;
}

/**
 * Record a token the SDK has just issued.
 *
 * AuthContext calls this from onIdTokenChanged, which fires on sign-in and on every background
 * refresh, so the cache is warm before the first save rather than after it and stays warm for the
 * life of the session.
 */
export function primeIdTokenCache(token: string | null): void {
  cachedIdToken = token;
}

/**
 * authedFetch, but the request leaves in the caller's own turn.
 *
 * authedFetch awaits getIdToken() before it reaches fetch(), so the request is only dispatched
 * from a continuation — and a continuation runs whenever the main thread next frees up. On the
 * home page the next thing after a save is a re-score of every programme, a second of blocked
 * thread, and the request was going out behind it. Using the cached token means fetch() is called
 * synchronously, before the caller can block anything.
 *
 * The cost is a token that may have just expired, so a 401 is retried once with a fresh one.
 */
export function authedFetchNow(
  input: RequestInfo | URL,
  init: RequestInit = {}
): Promise<Response> {
  const token = cachedIdToken;
  // Nothing cached yet (the very first request of a session) — no choice but to wait for one.
  if (!token) return authedFetch(input, init);

  const withToken = (bearer: string) => {
    const headers = new Headers(init.headers);
    headers.set("Authorization", `Bearer ${bearer}`);
    return fetch(input, { ...init, headers });
  };

  return withToken(token).then(async (response) => {
    if (response.status !== 401) return response;

    const fresh = await getIdToken();
    if (!fresh || fresh === token) return response;
    return withToken(fresh);
  });
}

export async function isSignedIn(): Promise<boolean> {
  return (await getIdToken()) !== null;
}

/**
 * fetch() with the caller's Firebase ID token attached.
 *
 * Signed-out callers still send the request (routes answer 401), so callers can
 * keep their existing "ignore 401" handling.
 */
export async function authedFetch(
  input: RequestInfo | URL,
  init: RequestInit = {}
): Promise<Response> {
  const token = await getIdToken();
  const headers = new Headers(init.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return fetch(input, { ...init, headers });
}
